// Mode-Based UX Sprint — per-USER cached experience-mode prefs (mirrors the
// useFeatureFlag pattern). Cache is scoped to the authenticated user_id and
// cleared on logout/account switch so mode state never leaks across accounts.
import { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';

let _cache = null;
let _inflight = null;
let _cacheUser; // undefined = not yet synced with AuthContext
let _generation = 0; // bumped on account switch/reset — stale responses discarded
const _subscribers = new Set();

const notifySubscribers = () => {
  _subscribers.forEach((fn) => { try { fn(); } catch { /* isolated subscriber error */ } });
};

const fetchPrefs = (force = false) => {
  if (force) { _cache = null; _inflight = null; }
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  // Generation binding (release-safety P0) — see useFeatureFlag.js.
  const gen = _generation;
  _inflight = api.get('/api/me/experience-mode')
    .then(d => {
      if (gen !== _generation) return _cache || {};
      _cache = d || {};
      return _cache;
    })
    .catch(() => {
      if (gen !== _generation) return _cache || {};
      _cache = {};
      return _cache;
    })
    .finally(() => { if (gen === _generation) _inflight = null; });
  return _inflight;
};

// Called by AuthContext whenever the authenticated user changes.
export const syncExperienceModeUser = (userId) => {
  const uid = userId || null;
  if (_cacheUser === undefined) { _cacheUser = uid; return; }
  if (_cacheUser === uid) return;
  _cacheUser = uid;
  _generation += 1;
  _cache = null;
  _inflight = null;
  notifySubscribers();
};

export const useExperienceMode = () => {
  const [state, setState] = useState({ ready: !!_cache, prefs: _cache || {} });
  useEffect(() => {
    let cancelled = false;
    const apply = (prefs) => { if (!cancelled) setState({ ready: true, prefs }); };
    fetchPrefs().then(apply);
    const onInvalidate = () => fetchPrefs(true).then(apply);
    _subscribers.add(onInvalidate);
    return () => { cancelled = true; _subscribers.delete(onInvalidate); };
  }, []);
  return state;
};

export const refreshExperienceMode = () => {
  _generation += 1;
  _cache = null;
  _inflight = null;
  notifySubscribers();
};
